import { FacilitiesAttribute, FacilitiesFact, FacilitiesGroup, Maybe } from '@gql/schema';
import { ITranslationsObject, i18n } from '@secretescapes/i18n';
import { notEmpty } from '@features/AvailabilityPresentation/AvailabilityPresentation.utils';
import { ATTRIBUTE_KEYS_TO_DISPLAY, FacilitiesPresentationLayers } from '@features/Sale/Facilities';
import { ATTRIBUTES_RANKINGS, FACILITIES_RANKINGS } from './Facilities.config';
import { ExtendedFacilitiesFact } from './Facilities.interfaces';

const ACCESSIBILITY_DISCLAIMER_FACILITY_KEY = 'generally_suitable_for_people_with_reduced_mobility';
const TOP_FACILITIES_GROUP_KEY = 'top_facilities';
const SKI_FACILITIES_GROUP_KEY = 'ski';
const FAMILY_FRIENDLY_FACILITIES_GROUP_KEY = 'family_friendly';
const SPA_AND_WELLNESS_FACILITY_KEY = 'spa_and_wellness_facilities';
const SPA_FACILITY_KEY = 'spa';

export const displayAttribute = (attribute: Maybe<FacilitiesAttribute>) => {
  return !!attribute?.name && ATTRIBUTE_KEYS_TO_DISPLAY.includes(attribute.key);
};

export const flattenMultipleFactsByAttributes = (arr: FacilitiesFact[]) => {
  return Object.values(
    arr.reduce<Record<string, FacilitiesFact>>((facilitiesFactRecords, fact) => {
      const uniqueKey = `${fact.key}-${JSON.stringify(fact.attributes)}`;

      const objectExistsInRecord = facilitiesFactRecords[uniqueKey];

      if (!objectExistsInRecord) {
        facilitiesFactRecords[uniqueKey] = { ...fact };
      } else {
        objectExistsInRecord.number = (objectExistsInRecord.number ?? 1) + (fact.number ?? 1);
      }

      return facilitiesFactRecords;
    }, {})
  );
};

export const flattenMultipleFactKeys = (arr: ExtendedFacilitiesFact[]) => {
  return Object.values(
    arr.reduce<Record<string, ExtendedFacilitiesFact>>((facilitiesFactRecords, fact) => {
      const objectExistsInRecord = facilitiesFactRecords[fact.key];
      const reducedFact = (({ number, name, key, groupKey }) => ({
        number,
        name,
        key,
        groupKey,
      }))(fact);

      if (!objectExistsInRecord) {
        facilitiesFactRecords[fact.key] = { ...reducedFact };
      } else {
        objectExistsInRecord.number = (objectExistsInRecord.number ?? 1) + (fact.number ?? 1);
      }

      return facilitiesFactRecords;
    }, {})
  );
};

export const flattenFacts = (facts: Maybe<Maybe<FacilitiesFact>[]>, groupKey: string) => {
  return facts?.filter(notEmpty).map(fact => ({ ...fact, groupKey })) ?? [];
};

export const createCustomFact = (key: string, translations: ITranslationsObject) => {
  return {
    key,
    groupKey: key,
    name: i18n(`fact_${key}_facilities`, [], translations),
  };
};

export const rankFacilities = (facilities: ExtendedFacilitiesFact[]) => {
  return [...facilities].sort(
    (a, b) => (FACILITIES_RANKINGS[a.key] ?? Infinity) - (FACILITIES_RANKINGS[b.key] ?? Infinity)
  );
};

export const rankAttributes = (attributes: FacilitiesAttribute[]) => {
  return [...attributes].sort(
    (a, b) => (ATTRIBUTES_RANKINGS[a.key] ?? 1) - (ATTRIBUTES_RANKINGS[b.key] ?? 1)
  );
};

export const getFlattenedRankedFacilities = (
  facilities: FacilitiesGroup[],
  translations: ITranslationsObject
) => {
  const hasFamilyFriendlyFacts = facilities.some(
    facilityGroup => facilityGroup?.key === FAMILY_FRIENDLY_FACILITIES_GROUP_KEY
  );

  const hasSkiFacts = facilities.some(
    facilityGroup => facilityGroup?.key === SKI_FACILITIES_GROUP_KEY
  );

  const ignoredKeys = [
    TOP_FACILITIES_GROUP_KEY,
    FAMILY_FRIENDLY_FACILITIES_GROUP_KEY,
    SKI_FACILITIES_GROUP_KEY,
  ];

  const facts = facilities
    .filter(facilityGroup => !ignoredKeys.includes(facilityGroup.key))
    .flatMap(facilityGroup => flattenFacts(facilityGroup.facts, facilityGroup.key))
    .filter(fact => !!fact?.name);

  const uniqueFactsByKey = flattenMultipleFactKeys(facts);

  const hasSpaFact = facts.some(fact => fact.key === SPA_FACILITY_KEY);

  const filteredFacts = [
    ...(hasSpaFact
      ? uniqueFactsByKey.filter(fact => fact.key !== SPA_AND_WELLNESS_FACILITY_KEY)
      : [...uniqueFactsByKey]),
    ...(hasFamilyFriendlyFacts
      ? [createCustomFact(FAMILY_FRIENDLY_FACILITIES_GROUP_KEY, translations)]
      : []),
    ...(hasSkiFacts ? [createCustomFact(SKI_FACILITIES_GROUP_KEY, translations)] : []),
  ];

  return rankFacilities(filteredFacts);
};

export const transformFacilities = (
  facilities: Maybe<Maybe<FacilitiesGroup>[]>,
  translations: ITranslationsObject
): FacilitiesPresentationLayers => {
  if (!facilities) {
    return {
      groupedFacilities: [],
      flattenedFacilities: [],
    };
  }

  const filteredFacilities = facilities.filter(notEmpty);

  const flattenedFacilities = getFlattenedRankedFacilities(filteredFacilities, translations);

  const flattenedFacilitiesWithoutDisclaimer = flattenedFacilities.filter(
    facility => facility.key !== ACCESSIBILITY_DISCLAIMER_FACILITY_KEY
  );

  const accessibilityDisclaimerFacility = flattenedFacilities.find(
    facility => facility.key === ACCESSIBILITY_DISCLAIMER_FACILITY_KEY
  );

  const groupedFacilitiesWithoutDisclaimer: FacilitiesGroup[] = filteredFacilities
    .filter(facilityGroup => facilityGroup.key !== TOP_FACILITIES_GROUP_KEY)
    .map(facilityGroup => {
      const filteredFacts = (facilityGroup.facts ?? [])
        .filter(notEmpty)
        .filter(fact => fact.key !== ACCESSIBILITY_DISCLAIMER_FACILITY_KEY)
        .map(fact => {
          const filteredAttributes = (fact.attributes ?? [])
            .filter(attr => displayAttribute(attr))
            .filter(notEmpty)
            .sort((a, b) => a.key.localeCompare(b.key));

          return {
            ...fact,
            attributes: rankAttributes(filteredAttributes),
          };
        });

      const uniqueFactsByKeyAndAttributes = flattenMultipleFactsByAttributes(filteredFacts);

      return {
        ...facilityGroup,
        facts: uniqueFactsByKeyAndAttributes,
      };
    })
    .filter(facilityGroup => facilityGroup.facts?.length);

  return {
    flattenedFacilities: flattenedFacilitiesWithoutDisclaimer,
    groupedFacilities: groupedFacilitiesWithoutDisclaimer,
    accessibilityFacility: accessibilityDisclaimerFacility,
  };
};
